// [NEW FILE] State + data for the shared Business Unit / Department / Location / Cost Centre / Legal Entity filters.
//
//   const org = useOrgFilters();
//   <OrgFilters org={org} />                          // renders the five dropdowns
//   rows.filter((r) => org.matches(r.employee))       // applies them to any list that has employees
//
// The employee directory (employeeApi.getOrgDirectory) is used to look up an employee's attributes by id, so it
// also works for rows whose employee object does not carry them (reports, review, reminders, calibration ...).
import { useCallback, useMemo, useState } from "react";
import useAsync from "./useAsync";
import { getOrgDirectory } from "../services/employeeApi";
import { EMPTY_ORG_FILTERS, hasOrgFilters, matchesOrg, orgOptions, toList } from "../utils/orgFilters";

const NO_EXTRA = [];

// `list`: pass a list of employees the screen already has (or null while it loads) to skip the directory request.
export default function useOrgFilters({ extra = NO_EXTRA, list: given } = {}) {
  const [filters, setFilters] = useState(EMPTY_ORG_FILTERS);
  const directory = useAsync(() => getOrgDirectory(), [], { skip: given !== undefined });
  const list = useMemo(() => (given !== undefined ? given || NO_EXTRA : directory.data || NO_EXTRA), [given, directory.data]);
  const byId = useMemo(() => Object.fromEntries(list.map((e) => [e.id, e])), [list]);
  // options come from the directory, plus any extra employees a screen already has (so nothing is missing)
  const options = useMemo(() => orgOptions([...list, ...extra]), [list, extra]);
  const active = hasOrgFilters(filters);

  // [UPDATED] PM-18: value is a list of chosen values (a single value is turned into a list)
  const set = useCallback((key, value) => setFilters((f) => ({ ...f, [key]: toList(value) })), []);
  const clear = useCallback(() => setFilters(EMPTY_ORG_FILTERS), []);
  // emp: an employee object ({ id, ... }) or an employee id
  const matches = useCallback((emp) => {
    if (!active) return true;
    const own = typeof emp === "string" ? { id: emp } : emp || {};
    return matchesOrg({ ...own, ...(byId[own.id] || {}) }, filters);
  }, [active, byId, filters]);

  return { filters, set, clear, active, options, matches, loading: given !== undefined ? false : directory.loading, directory: byId };
}
